import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  X, Upload, FolderPlus, Search, Tag, Trash2, Download,
  FileText, Image, File, FolderOpen, Share2, Link, Users,
  ChevronRight, Loader2, Filter, Grid3X3, List, MoreVertical,
  Globe, Lock, Check, Plus, Edit3
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FILE_ICONS = {
  'application/pdf': { icon: FileText, color: 'text-red-500' },
  'image/jpeg': { icon: Image, color: 'text-blue-500' },
  'image/png': { icon: Image, color: 'text-blue-500' },
  'image/gif': { icon: Image, color: 'text-blue-500' },
  'image/webp': { icon: Image, color: 'text-blue-500' },
  'text/plain': { icon: FileText, color: 'text-gray-500' },
  'text/markdown': { icon: FileText, color: 'text-purple-500' },
  'text/csv': { icon: FileText, color: 'text-green-500' },
};

function formatSize(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export default function DocumentStorage({ onClose }) {
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFolder, setActiveFolder] = useState(null);
  const [activeTag, setActiveTag] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [uploading, setUploading] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showShareModal, setShowShareModal] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const fileInputRef = useRef(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeFolder) params.append('folder_id', activeFolder);
      if (activeTag) params.append('tag', activeTag);
      if (searchQuery) params.append('search', searchQuery);
      if (sourceFilter) params.append('source', sourceFilter);

      const [docsRes, foldersRes, tagsRes] = await Promise.all([
        axios.get(`${API}/storage/documents?${params}`),
        axios.get(`${API}/storage/folders`),
        axios.get(`${API}/storage/tags`)
      ]);
      setDocuments(docsRes.data.documents || []);
      setFolders(foldersRes.data.folders || []);
      setTags(tagsRes.data.tags || []);
    } catch (err) {
      console.error('Failed to load storage:', err);
    } finally {
      setLoading(false);
    }
  }, [activeFolder, activeTag, searchQuery, sourceFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder_id', activeFolder || '');
        formData.append('tags', '');
        await axios.post(`${API}/storage/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await axios.post(`${API}/storage/folders`, {
        name: newFolderName.trim(),
        parent_id: activeFolder
      });
      setNewFolderName('');
      setShowNewFolder(false);
      loadData();
    } catch (err) {
      alert('Failed to create folder');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Move this document to trash?')) return;
    try {
      await axios.delete(`${API}/storage/documents/${docId}`);
      loadData();
    } catch (err) {
      alert('Failed to delete');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Documents will be moved to root.')) return;
    try {
      await axios.delete(`${API}/storage/folders/${folderId}`);
      if (activeFolder === folderId) setActiveFolder(null);
      loadData();
    } catch (err) {
      alert('Failed to delete folder');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/storage/documents/${doc.id}/download`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename || doc.title;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed');
    }
  };

  const handleShare = async (docId, type) => {
    try {
      const res = await axios.put(`${API}/storage/documents/${docId}/share`, { type });
      setShowShareModal(res.data);
      loadData();
    } catch (err) {
      alert('Failed to share');
    }
  };

  const handleMoveToFolder = async (docId, folderId) => {
    try {
      await axios.put(`${API}/storage/documents/${docId}`, { folder_id: folderId || '' });
      setContextMenu(null);
      loadData();
    } catch (err) {
      alert('Failed to move');
    }
  };

  const getFileIcon = (ct) => {
    const config = FILE_ICONS[ct] || { icon: File, color: 'text-[#475569]' };
    return config;
  };

  const activeFolderName = folders.find(f => f.id === activeFolder)?.name;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-2 sm:p-4" data-testid="storage-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-[#E2DFD8]">
          <div>
            <h2 className="font-heading text-lg sm:text-xl font-bold text-[#1B3A6B]">Document Storage</h2>
            <p className="text-xs sm:text-sm text-[#475569]">{documents.length} documents{activeFolderName ? ` in ${activeFolderName}` : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              multiple
              accept=".pdf,.doc,.docx,.pptx,.xlsx,.txt,.csv,.md,.jpg,.jpeg,.png,.gif,.webp"
              onChange={handleUpload}
              data-testid="storage-file-input"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium hover:bg-[#254B8A] transition-colors disabled:opacity-50"
              data-testid="storage-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              <span className="hidden sm:inline">{uploading ? 'Uploading...' : 'Upload'}</span>
            </button>
            <button onClick={onClose} className="p-2 hover:bg-[#F4F2EC] rounded-lg" data-testid="storage-close-btn">
              <X className="w-5 h-5 text-[#475569]" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <div className="hidden sm:flex w-56 border-r border-[#E2DFD8] flex-col bg-[#FAFAF8] overflow-y-auto">
            <div className="p-3 space-y-1">
              <button
                onClick={() => { setActiveFolder(null); setActiveTag(''); setSourceFilter(''); }}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${!activeFolder && !activeTag && !sourceFilter ? 'bg-[#1B3A6B] text-white' : 'text-[#475569] hover:bg-[#F4F2EC]'}`}
                data-testid="storage-all-docs"
              >
                <FolderOpen className="w-4 h-4" /> All Documents
              </button>
              <button
                onClick={() => { setActiveFolder(null); setActiveTag(''); setSourceFilter('upload'); }}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${sourceFilter === 'upload' ? 'bg-[#1B3A6B] text-white' : 'text-[#475569] hover:bg-[#F4F2EC]'}`}
                data-testid="storage-uploads-filter"
              >
                <Upload className="w-4 h-4" /> Uploads
              </button>
              <button
                onClick={() => { setActiveFolder(null); setActiveTag(''); setSourceFilter('generated'); }}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${sourceFilter === 'generated' ? 'bg-[#1B3A6B] text-white' : 'text-[#475569] hover:bg-[#F4F2EC]'}`}
                data-testid="storage-generated-filter"
              >
                <FileText className="w-4 h-4" /> AI Generated
              </button>
            </div>

            {/* Folders */}
            <div className="px-3 pt-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Folders</span>
                <button onClick={() => setShowNewFolder(true)} className="p-1 hover:bg-[#F4F2EC] rounded" data-testid="storage-new-folder-btn">
                  <Plus className="w-3.5 h-3.5 text-[#475569]" />
                </button>
              </div>
              {showNewFolder && (
                <div className="flex gap-1 mb-1">
                  <input
                    value={newFolderName}
                    onChange={e => setNewFolderName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleCreateFolder()}
                    placeholder="Folder name"
                    className="flex-1 text-xs px-2 py-1.5 border border-[#E2DFD8] rounded"
                    autoFocus
                    data-testid="storage-folder-name-input"
                  />
                  <button onClick={handleCreateFolder} className="p-1 text-[#1B3A6B]"><Check className="w-3.5 h-3.5" /></button>
                  <button onClick={() => setShowNewFolder(false)} className="p-1 text-[#475569]"><X className="w-3.5 h-3.5" /></button>
                </div>
              )}
              <div className="space-y-0.5">
                {folders.map(f => (
                  <div key={f.id} className="group flex items-center">
                    <button
                      onClick={() => { setActiveFolder(f.id); setActiveTag(''); setSourceFilter(''); }}
                      className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeFolder === f.id ? 'bg-[#1B3A6B] text-white' : 'text-[#475569] hover:bg-[#F4F2EC]'}`}
                      data-testid={`storage-folder-${f.id}`}
                    >
                      <FolderOpen className="w-4 h-4" style={{ color: activeFolder === f.id ? 'white' : f.color }} />
                      <span className="truncate">{f.name}</span>
                    </button>
                    <button onClick={() => handleDeleteFolder(f.id)} className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-500 transition-all">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Tags */}
            {tags.length > 0 && (
              <div className="px-3 pt-4">
                <span className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Tags</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {tags.map(t => (
                    <button
                      key={t.name}
                      onClick={() => { setActiveTag(activeTag === t.name ? '' : t.name); setActiveFolder(null); setSourceFilter(''); }}
                      className={`px-2 py-0.5 rounded-full text-xs transition-colors ${activeTag === t.name ? 'bg-[#1B3A6B] text-white' : 'bg-[#F4F2EC] text-[#475569] hover:bg-[#E2DFD8]'}`}
                      data-testid={`storage-tag-${t.name}`}
                    >
                      {t.name} ({t.count})
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Main content area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E2DFD8]">
              {/* Mobile folder/filter selector */}
              <select
                className="sm:hidden text-sm border border-[#E2DFD8] rounded-lg px-2 py-2 bg-white"
                value={activeFolder || sourceFilter || 'all'}
                onChange={e => {
                  const v = e.target.value;
                  if (v === 'all') { setActiveFolder(null); setSourceFilter(''); }
                  else if (v === 'upload' || v === 'generated') { setActiveFolder(null); setSourceFilter(v); }
                  else { setActiveFolder(v); setSourceFilter(''); }
                }}
                data-testid="storage-mobile-filter"
              >
                <option value="all">All Documents</option>
                <option value="upload">Uploads</option>
                <option value="generated">AI Generated</option>
                {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>

              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search documents..."
                  className="w-full pl-9 pr-3 py-2 text-sm bg-[#F9F8F6] border-none rounded-lg focus:ring-2 focus:ring-[#1B3A6B]"
                  data-testid="storage-search"
                />
              </div>
              <div className="hidden sm:flex gap-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-[#1B3A6B] text-white' : 'text-[#475569] hover:bg-[#F4F2EC]'}`}
                  data-testid="storage-view-grid"
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-[#1B3A6B] text-white' : 'text-[#475569] hover:bg-[#F4F2EC]'}`}
                  data-testid="storage-view-list"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
              {/* Mobile upload */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="sm:hidden p-2 bg-[#1B3A6B] text-white rounded-lg"
                data-testid="storage-mobile-upload"
              >
                <Upload className="w-4 h-4" />
              </button>
            </div>

            {/* Breadcrumb */}
            {activeFolder && (
              <div className="flex items-center gap-1 px-4 py-2 text-sm text-[#475569]">
                <button onClick={() => setActiveFolder(null)} className="hover:text-[#1B3A6B]">All</button>
                <ChevronRight className="w-3 h-3" />
                <span className="font-medium text-[#0F1A2A]">{activeFolderName}</span>
              </div>
            )}

            {/* Documents */}
            <div className="flex-1 overflow-y-auto p-4">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-6 h-6 animate-spin text-[#1B3A6B]" />
                </div>
              ) : documents.length === 0 ? (
                <div className="text-center py-16">
                  <FolderOpen className="w-14 h-14 mx-auto mb-4 text-[#D1CBC0]" />
                  <p className="text-[#475569] font-medium mb-1">No documents yet</p>
                  <p className="text-sm text-[#94A3B8] mb-4">Upload files or save AI-generated content</p>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-5 py-2.5 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium hover:bg-[#254B8A]"
                    data-testid="storage-empty-upload"
                  >
                    Upload First Document
                  </button>
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {documents.map(doc => {
                    const { icon: Icon, color } = getFileIcon(doc.content_type);
                    return (
                      <div key={doc.id} className="group bg-white border border-[#E2DFD8] rounded-xl p-4 hover:shadow-md hover:border-[#1B3A6B]/30 transition-all relative" data-testid={`storage-doc-${doc.id}`}>
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg bg-[#F4F2EC] flex items-center justify-center flex-shrink-0 ${color}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-[#0F1A2A] text-sm truncate">{doc.title}</h4>
                            <p className="text-xs text-[#94A3B8] mt-0.5">
                              {formatSize(doc.size)} · {new Date(doc.created_at).toLocaleDateString()}
                            </p>
                            {doc.source === 'generated' && (
                              <span className="inline-block mt-1 text-xs px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded">AI Generated</span>
                            )}
                            {doc._shared && (
                              <span className="inline-block mt-1 text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded ml-1">Shared</span>
                            )}
                          </div>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => handleDownload(doc)} className="p-1.5 hover:bg-[#F4F2EC] rounded" title="Download">
                              <Download className="w-3.5 h-3.5 text-[#475569]" />
                            </button>
                            <button onClick={() => setShowShareModal(doc)} className="p-1.5 hover:bg-[#F4F2EC] rounded" title="Share">
                              <Share2 className="w-3.5 h-3.5 text-[#475569]" />
                            </button>
                            {!doc._shared && (
                              <button onClick={() => handleDelete(doc.id)} className="p-1.5 hover:bg-red-50 rounded" title="Delete">
                                <Trash2 className="w-3.5 h-3.5 text-[#475569] hover:text-red-500" />
                              </button>
                            )}
                          </div>
                        </div>
                        {doc.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {doc.tags.map(t => (
                              <span key={t} className="px-1.5 py-0.5 text-[10px] bg-[#F4F2EC] text-[#475569] rounded">
                                {t}
                              </span>
                            ))}
                          </div>
                        )}
                        {(doc.is_public || doc.is_shared_org) && (
                          <div className="absolute top-2 right-2">
                            {doc.is_public ? <Globe className="w-3.5 h-3.5 text-[#B8860B]" /> : <Users className="w-3.5 h-3.5 text-blue-500" />}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-1">
                  {documents.map(doc => {
                    const { icon: Icon, color } = getFileIcon(doc.content_type);
                    return (
                      <div key={doc.id} className="group flex items-center gap-3 px-4 py-3 hover:bg-[#F9F8F6] rounded-lg transition-colors" data-testid={`storage-doc-${doc.id}`}>
                        <div className={`w-8 h-8 rounded-lg bg-[#F4F2EC] flex items-center justify-center flex-shrink-0 ${color}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-[#0F1A2A] text-sm truncate">{doc.title}</h4>
                        </div>
                        <span className="text-xs text-[#94A3B8] hidden sm:inline">{formatSize(doc.size)}</span>
                        <span className="text-xs text-[#94A3B8] hidden sm:inline">{new Date(doc.created_at).toLocaleDateString()}</span>
                        {doc.source === 'generated' && <span className="text-xs px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded hidden sm:inline">AI</span>}
                        {(doc.is_public || doc.is_shared_org) && (
                          doc.is_public ? <Globe className="w-3.5 h-3.5 text-[#B8860B]" /> : <Users className="w-3.5 h-3.5 text-blue-500" />
                        )}
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => handleDownload(doc)} className="p-1.5 hover:bg-[#F4F2EC] rounded"><Download className="w-3.5 h-3.5 text-[#475569]" /></button>
                          <button onClick={() => setShowShareModal(doc)} className="p-1.5 hover:bg-[#F4F2EC] rounded"><Share2 className="w-3.5 h-3.5 text-[#475569]" /></button>
                          {!doc._shared && <button onClick={() => handleDelete(doc.id)} className="p-1.5 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5 text-[#475569] hover:text-red-500" /></button>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Share Modal */}
        {showShareModal && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center z-10">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4" data-testid="share-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#0F1A2A]">Share Document</h3>
                <button onClick={() => setShowShareModal(null)}><X className="w-5 h-5 text-[#475569]" /></button>
              </div>
              <p className="text-sm text-[#475569] mb-4 truncate">{showShareModal.title}</p>

              <div className="space-y-2">
                <button
                  onClick={() => handleShare(showShareModal.id, 'org')}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${showShareModal.is_shared_org ? 'border-blue-300 bg-blue-50' : 'border-[#E2DFD8] hover:border-blue-300'}`}
                  data-testid="share-org-btn"
                >
                  <Users className="w-5 h-5 text-blue-500" />
                  <div className="text-left">
                    <p className="text-sm font-medium">Share with Organization</p>
                    <p className="text-xs text-[#94A3B8]">Visible to your church members</p>
                  </div>
                  {showShareModal.is_shared_org && <Check className="w-4 h-4 text-blue-500 ml-auto" />}
                </button>

                <button
                  onClick={() => handleShare(showShareModal.id, 'public')}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${showShareModal.is_public ? 'border-[#B8860B] bg-amber-50' : 'border-[#E2DFD8] hover:border-[#B8860B]'}`}
                  data-testid="share-public-btn"
                >
                  <Globe className="w-5 h-5 text-[#B8860B]" />
                  <div className="text-left">
                    <p className="text-sm font-medium">Public Link</p>
                    <p className="text-xs text-[#94A3B8]">Anyone with the link can view</p>
                  </div>
                  {showShareModal.is_public && <Check className="w-4 h-4 text-[#B8860B] ml-auto" />}
                </button>

                {showShareModal.share_id && (
                  <div className="mt-3 p-3 bg-[#F9F8F6] rounded-lg">
                    <p className="text-xs text-[#475569] mb-1">Public link:</p>
                    <div className="flex gap-2">
                      <input
                        readOnly
                        value={`${window.location.origin}/shared/doc/${showShareModal.share_id}`}
                        className="flex-1 text-xs px-2 py-1.5 bg-white border border-[#E2DFD8] rounded"
                        data-testid="share-link-input"
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/shared/doc/${showShareModal.share_id}`);
                          alert('Link copied!');
                        }}
                        className="px-3 py-1.5 bg-[#1B3A6B] text-white rounded text-xs"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                )}

                {(showShareModal.is_public || showShareModal.is_shared_org) && (
                  <button
                    onClick={() => handleShare(showShareModal.id, 'none')}
                    className="w-full flex items-center gap-3 p-3 rounded-lg border border-[#E2DFD8] hover:border-red-300 transition-colors"
                    data-testid="share-remove-btn"
                  >
                    <Lock className="w-5 h-5 text-[#475569]" />
                    <div className="text-left">
                      <p className="text-sm font-medium">Make Private</p>
                      <p className="text-xs text-[#94A3B8]">Remove all sharing</p>
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
